import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { ROLES } from '../services/authService';
import './AccessRequests.css';

const ROLE_OPTIONS = [
  { value: ROLES.TECHNICIAN, label: 'Technician', icon: '🔧' },
  { value: ROLES.SUPERVISOR, label: 'Supervisor', icon: '🎯' },
  { value: ROLES.ADMIN,      label: 'Admin',      icon: '👑' }
];

function AccessRequests({ user }) {
  const [pending, setPending]         = useState([]);
  const [decided, setDecided]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('pending'); // pending | approved | denied
  const [selectedRole, setSelectedRole] = useState({}); // { [uid]: role }
  const [busyId, setBusyId]           = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'users'), where('approvalStatus', 'in', ['pending', 'approved', 'denied']));
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPending(all.filter(u => u.approvalStatus === 'pending'));
      setDecided(all.filter(u => u.approvalStatus !== 'pending'));
      setLoading(false);
    }, (err) => {
      console.error('Error loading access requests:', err);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const roleFor = (uid) => selectedRole[uid] || ROLES.TECHNICIAN;

  const handleApprove = async (request) => {
    setBusyId(request.id);
    try {
      await updateDoc(doc(db, 'users', request.id), {
        approvalStatus: 'approved',
        active: true,
        role: roleFor(request.id),
        approvedBy: user.name,
        approvedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error approving user:', err);
      alert('Something went wrong approving this request. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeny = async (request) => {
    if (!window.confirm(`Deny access for ${request.name}? They will not be able to log in.`)) {
      return;
    }
    setBusyId(request.id);
    try {
      await updateDoc(doc(db, 'users', request.id), {
        approvalStatus: 'denied',
        active: false,
        deniedBy: user.name,
        deniedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error denying user:', err);
      alert('Something went wrong denying this request. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return 'Just now';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-KE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const list = filter === 'pending' ? pending : decided.filter(u => u.approvalStatus === filter);

  return (
    <div className="access-requests">
      <div className="ar-header">
        <h2>🔑 Access Requests</h2>
        {pending.length > 0 && <span className="ar-badge">{pending.length} waiting</span>}
      </div>

      <div className="ar-filters">
        <button className={`ar-pill ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
          Pending ({pending.length})
        </button>
        <button className={`ar-pill ${filter === 'approved' ? 'active' : ''}`} onClick={() => setFilter('approved')}>
          Approved ({decided.filter(u => u.approvalStatus === 'approved').length})
        </button>
        <button className={`ar-pill ${filter === 'denied' ? 'active' : ''}`} onClick={() => setFilter('denied')}>
          Denied ({decided.filter(u => u.approvalStatus === 'denied').length})
        </button>
      </div>

      {loading ? (
        <div className="ar-empty">Loading requests...</div>
      ) : list.length === 0 ? (
        <div className="ar-empty">
          <div className="ar-empty-icon">📭</div>
          <p>No {filter} requests</p>
        </div>
      ) : (
        <div className="ar-list">
          {list.map(req => (
            <div key={req.id} className="ar-card">
              <div className="ar-card-main">
                <div className="ar-avatar">{req.name?.charAt(0).toUpperCase() || '?'}</div>
                <div className="ar-info">
                  <div className="ar-name">{req.name}</div>
                  <div className="ar-email">{req.email}</div>
                  <div className="ar-date">Requested {formatDate(req.createdAt)}</div>
                </div>
                {req.approvalStatus !== 'pending' && (
                  <div className={`ar-status-badge ${req.approvalStatus}`}>
                    {req.approvalStatus === 'approved' ? `✅ ${req.role}` : '🚫 Denied'}
                  </div>
                )}
              </div>

              {req.approvalStatus === 'pending' && (
                <div className="ar-actions">
                  <select
                    className="ar-role-select"
                    value={roleFor(req.id)}
                    onChange={(e) => setSelectedRole(prev => ({ ...prev, [req.id]: e.target.value }))}
                    disabled={busyId === req.id}
                  >
                    {ROLE_OPTIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.icon} {r.label}</option>
                    ))}
                  </select>
                  <button
                    className="ar-btn ar-approve"
                    onClick={() => handleApprove(req)}
                    disabled={busyId === req.id}
                  >
                    {busyId === req.id ? '...' : '✅ Approve'}
                  </button>
                  <button
                    className="ar-btn ar-deny"
                    onClick={() => handleDeny(req)}
                    disabled={busyId === req.id}
                  >
                    🚫 Deny
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AccessRequests;
